import { FieldArrayWithId } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { HookFormControlShim, Lens } from "@hookform/lenses"
import { z } from "zod"
import { Banner } from "@/components/ui/banner"
import { DetailCheckbox } from "@/components/ui/checkbox"
import { FormField } from "@/components/ui/form"

export const episodeOfCareRelatedConditionsFormSchema = z.object({
  relatedConditions: z.array(
    z.object({
      icdCode: z.string(),
      description: z.string().nullish().optional(),
      value: z.boolean(),
      isFromIcdSelectInThisSession: z.boolean(),
    }),
  ),
})

export type EpisodeOfCareRelatedConditionschema = z.infer<typeof episodeOfCareRelatedConditionsFormSchema>

export type LinkedConditionInformation = {
  diagnosisCode: string
  text: string
}

type RelatedConditionsFormProps = {
  lens: Lens<EpisodeOfCareRelatedConditionschema["relatedConditions"]>
  fields: FieldArrayWithId<
    HookFormControlShim<EpisodeOfCareRelatedConditionschema["relatedConditions"]>,
    // This is the type that react-hook-form gives us when we use useFieldArray.
    // Normally we want to pass the lens down and use it to get the array but the is a bug with the library so the child component don't have the same data as the useFieldArray in the parent component.
    // Github issue: https://github.com/react-hook-form/lenses/issues/32
    "__DO_NOT_USE_HOOK_FORM_CONTROL_SHIM__",
    "id"
  >[]
  linkedConditionInformation?: LinkedConditionInformation
}

const RelatedConditionsForm = ({ lens, fields, linkedConditionInformation }: RelatedConditionsFormProps) => {
  const { t } = useTranslation()

  return (
    fields.length !== 0 && (
      <div className="flex flex-col gap-2">
        <label className="text-on-surface-subdued text-sm">
          {t("episodeOfCare.editEpisodeOfCare.relatedConditions")}
        </label>

        {linkedConditionInformation && (
          <Banner
            data-testid="linked-condition-information"
            type="inline"
            variant="info"
            className="flex items-center gap-1 rounded-md">
            {t("episodeOfCare.editEpisodeOfCare.linkedCondition", {
              text: `${linkedConditionInformation.diagnosisCode} - ${linkedConditionInformation.text}`,
            })}
          </Banner>
        )}

        <div className="flex flex-wrap gap-2">
          {lens.map(fields, (field, itemLens) => (
            <FormField
              key={field.id}
              {...itemLens.focus("value").interop((ctrl, name) => ctrl.register(name))}
              render={({ field: { onChange, value } }) => (
                <DetailCheckbox
                  data-testid={`episodeOfCare-relatedCondition-${field.icdCode}`}
                  checked={value}
                  onCheckedChange={onChange}>
                  {field.description}
                </DetailCheckbox>
              )}
            />
          ))}
        </div>
      </div>
    )
  )
}

export { RelatedConditionsForm }
